import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input } from "@/components/ui/Primitives";
import { Building2, Plus, MapPin, Home, ExternalLink, Trash2, Megaphone, Database, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { fireActivation } from "@/lib/google-tag";
import { confirmDialog } from "@/components/ConfirmDialog";

export default function Properties() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(params.get("add") === "1");
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({
    name: "", address: "", image_url: "", units_total: 1,
    property_type: "residential",
    // Commercial-only fields (ignored on residential)
    cam_charges_dollars: "",
    is_nnn: false,
    gst_hst_registered: false,
    gst_hst_number: "",
    gst_hst_rate_pct: "",
  });
  const [saving, setSaving] = useState(false);
  const [dismissedMigrate, setDismissedMigrate] = useState(() => {
    try { return localStorage.getItem("crc_migration_cta_dismissed") === "1"; } catch { return false; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/properties").catch(() => ({ data: [] }));
      setItems(Array.isArray(data) ? data : (data?.properties || []));
    } finally { setLoading(false); }
  };

  const removeProperty = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const target = items.find((p) => p.property_id === id);
    const ok = await confirmDialog({
      title: "Delete this property?",
      description: `This removes ${target?.name || "the property"} along with its units and public listing.\n\nThis cannot be undone.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Keep property",
      destructive: true,
    });
    if (!ok) return;
    setDeleting(id);
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally { setDeleting(null); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Capture pre-creation count so we can fire the activation event
      // ONLY on the first property — every subsequent add is a no-op.
      const wasEmpty = items.length === 0;
      const isCommercial = form.property_type === "commercial" || form.property_type === "mixed_use";
      await api.post("/properties", {
        name: form.name.trim(),
        address: form.address.trim(),
        image_url: form.image_url.trim() || null,
        units_total: Number(form.units_total) || 1,
        property_type: form.property_type,
        commercial: isCommercial ? {
          cam_charges_cents_monthly: form.cam_charges_dollars ? Math.round(Number(form.cam_charges_dollars) * 100) : null,
          is_nnn: !!form.is_nnn,
          gst_hst_registered: !!form.gst_hst_registered,
          gst_hst_number: form.gst_hst_number.trim() || null,
          gst_hst_rate_pct: form.gst_hst_rate_pct ? Number(form.gst_hst_rate_pct) : null,
        } : null,
      });
      toast.success("Property created");
      if (wasEmpty && user?.role === "landlord") {
        fireActivation({ portfolioSize: 1, planKey: user?.subscription_status || "" });
      }
      setOpen(false);
      setForm({
        name: "", address: "", image_url: "", units_total: 1,
        property_type: "residential",
        cam_charges_dollars: "", is_nnn: false,
        gst_hst_registered: false, gst_hst_number: "", gst_hst_rate_pct: "",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create property");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  const isLandlord = user?.role === "landlord";

  return (
    <div>
      <PageHeader
        title="Properties"
        subtitle={isLandlord ? "Your portfolio of buildings and units." : "Properties available to you."}
        actions={isLandlord && (
          <Button onClick={() => setOpen(true)} data-testid="add-property-button">
            <Plus className="w-4 h-4"/> Add property
          </Button>
        )}
      />

      {/* Buildium (and general) migration CTA — surfaced on the property
          list so every landlord discovers the bulk-import path instead
          of manually adding units one by one. Dismissible via
          localStorage so it stops nagging once the landlord has enough
          properties. */}
      {isLandlord && items.length < 5 && !dismissedMigrate && (
        <div
          data-testid="migration-cta-banner"
          className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50/40 px-4 py-3 sm:px-5 sm:py-4"
        >
          <div className="flex items-start gap-3">
            <div className="flex-none w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm">
              <Database className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">
                Coming from Buildium, AppFolio, Yardi or a spreadsheet?
              </div>
              <div className="text-xs sm:text-sm text-slate-600 mt-0.5">
                Bulk-import your whole portfolio in one CSV. Undo is one click if the mapping goes sideways.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-none">
            <Link
              to="/migration?provider=buildium"
              data-testid="migration-cta-buildium"
              className="inline-flex items-center gap-1 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-sm font-medium"
            >
              Import from Buildium <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/migration"
              data-testid="migration-cta-all"
              className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 hover:bg-white text-slate-700 rounded-lg text-sm font-medium"
            >
              Other tools
            </Link>
            <button
              type="button"
              aria-label="Dismiss migration banner"
              data-testid="migration-cta-dismiss"
              onClick={() => {
                try { localStorage.setItem("crc_migration_cta_dismissed", "1"); } catch (_) { /* noop */ }
                setDismissedMigrate(true);
              }}
              className="text-slate-400 hover:text-slate-700 text-xs px-2 py-1"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div data-testid="properties-empty-with-migrate">
          <EmptyState
            icon={Building2}
            title="No properties yet"
            description={isLandlord ? "Add your first property below — or bulk-import your whole portfolio from Buildium, AppFolio, Yardi, or a CSV." : "Your landlord has not added properties yet."}
            action={null}
          />
          {isLandlord && (
            <div className="max-w-xl mx-auto -mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 mb-6">
              <Button onClick={() => setOpen(true)} data-testid="empty-add-property-button">
                <Plus className="w-4 h-4"/> Add one property
              </Button>
              <Link
                to="/migration?provider=buildium"
                data-testid="empty-import-buildium-button"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50 text-sm font-semibold"
              >
                <Database className="w-4 h-4"/> Bulk-import from Buildium
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p) => (
            <div key={p.property_id} className="relative group">
            <Link to={`/properties/${p.property_id}`} data-testid={`property-card-${p.property_id}`}
              className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-emerald-300 hover:-translate-y-0.5 transition-all duration-300 block h-full">
              <div className="aspect-[16/10] bg-gradient-to-br from-emerald-50 via-white to-sky-50 overflow-hidden relative">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} loading="lazy" decoding="async"
                       className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-[42px] font-display font-black tracking-tight text-slate-300 select-none uppercase">
                      {(p.name || p.address || "?").trim().charAt(0)}
                    </div>
                  </div>
                )}
                {/* Type + unit-count pills float on top-left of image so the
                    body copy below stays clean. */}
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-slate-800 text-[11px] font-bold shadow-sm">
                    <Home className="w-3 h-3"/> {p.units_total} {p.units_total === 1 ? "unit" : "units"}
                  </span>
                  {p.property_type && p.property_type !== "residential" && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold shadow-sm"
                          data-testid={`property-badge-${p.property_id}`}>
                      {p.property_type === "commercial" ? "Commercial" : "Mixed-use"}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-bold text-slate-900 leading-tight">{p.name}</h3>
                <div className="mt-2 flex items-start gap-1.5 text-sm text-slate-500">
                  <MapPin className="w-4 h-4 mt-0.5 flex-none text-slate-400"/>
                  <span className="line-clamp-2">{p.address}</span>
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span className="font-medium">Added {formatDate(p.created_at)}</span>
                  <div className="flex items-center gap-3">
                    {isLandlord && (
                      <button type="button"
                        onClick={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          try { sessionStorage.setItem("crc_smart_leasing_property", JSON.stringify({ id: p.property_id, name: p.name, address: p.address, image_url: p.image_url || "" })); } catch {}
                          window.location.href = `/smart?tab=leasing`;
                        }}
                        data-testid={`distribute-property-${p.property_id}`}
                        className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 hover:underline font-semibold">
                        <Megaphone className="w-3 h-3"/> Distribute
                      </button>
                    )}
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/p/${p.property_id}`, "_blank", "noreferrer"); }}
                      className="inline-flex items-center gap-1 text-emerald-700 hover:underline font-semibold">
                      Public page <ExternalLink className="w-3 h-3"/>
                    </button>
                  </div>
                </div>
              </div>
            </Link>
            {isLandlord && (
              <button
                type="button"
                onClick={(e) => removeProperty(e, p.property_id)}
                disabled={deleting === p.property_id}
                data-testid={`delete-property-${p.property_id}`}
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 hover:bg-red-600 hover:text-white backdrop-blur text-red-600 text-xs font-bold shadow-md z-10 transition-colors border border-red-200 hover:border-red-600"
              >
                <Trash2 className="w-3.5 h-3.5"/> {deleting === p.property_id ? "Deleting…" : "Delete"}
              </button>
            )}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add a new property" testid="add-property-modal">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Property name" required placeholder="Bayview Residences" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} testid="property-name-input"/>
          <Input label="Address" required placeholder="120 Marina Blvd, Toronto, ON" value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} testid="property-address-input"/>
          {/* Cover image — camera capture on mobile, file picker on desktop.
              Falls back to the URL field for landlords who already have a
              hosted image. Encoded to a data URL client-side and posted
              alongside the property. */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5">Cover image (optional)</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="property-image-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 6 * 1024 * 1024) {
                  toast.error("Image is too large — keep it under 6 MB.");
                  e.target.value = "";
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => setForm((s) => ({ ...s, image_url: String(reader.result || "") }));
                reader.onerror = () => toast.error("Couldn't read that image.");
                reader.readAsDataURL(f);
              }}
              className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-bold hover:file:bg-emerald-100"
            />
            {form.image_url && (
              <div className="mt-2 relative inline-block" data-testid="property-image-preview">
                <img src={form.image_url} alt="Cover preview" className="w-28 h-20 object-cover rounded-md border border-slate-200"/>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, image_url: "" })}
                  data-testid="property-image-remove"
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-300 shadow text-slate-500 hover:text-rose-600 text-xs font-bold leading-none"
                >×</button>
              </div>
            )}
            <div className="mt-2">
              <Input label="…or paste a hosted URL" placeholder="https://…" value={form.image_url.startsWith("data:") ? "" : form.image_url} onChange={(e)=>setForm({...form, image_url:e.target.value})} testid="property-image-input"/>
            </div>
          </div>
          <Input label="Total units" type="number" min="1" value={form.units_total} onChange={(e)=>setForm({...form, units_total:e.target.value})} testid="property-units-input"/>

          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5">Property type</label>
            <div className="grid grid-cols-3 gap-2" data-testid="property-type-group">
              {[
                { key: "residential", label: "Residential" },
                { key: "commercial", label: "Commercial" },
                { key: "mixed_use", label: "Mixed-use" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`property-type-${opt.key}`}
                  onClick={() => setForm({ ...form, property_type: opt.key })}
                  className={`px-3 py-2 rounded-md border text-sm font-semibold transition-colors ${
                    form.property_type === opt.key
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {(form.property_type === "commercial" || form.property_type === "mixed_use") && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="commercial-fields">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Commercial terms</div>
              <Input
                label="CAM charges (per month, CAD)"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 500"
                value={form.cam_charges_dollars}
                onChange={(e) => setForm({ ...form, cam_charges_dollars: e.target.value })}
                testid="cam-charges-input"
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.is_nnn}
                  onChange={(e) => setForm({ ...form, is_nnn: e.target.checked })}
                  data-testid="is-nnn-checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                Triple-net (NNN) — tenant pays tax + insurance + CAM
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.gst_hst_registered}
                  onChange={(e) => setForm({ ...form, gst_hst_registered: e.target.checked })}
                  data-testid="gst-hst-registered-checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                GST / HST registered
              </label>
              {form.gst_hst_registered && (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="CRA business number"
                    placeholder="123456789 RT0001"
                    value={form.gst_hst_number}
                    onChange={(e) => setForm({ ...form, gst_hst_number: e.target.value })}
                    testid="gst-hst-number-input"
                  />
                  <Input
                    label="Rate %"
                    type="number"
                    min="0"
                    max="25"
                    step="0.01"
                    placeholder="e.g. 13"
                    value={form.gst_hst_rate_pct}
                    onChange={(e) => setForm({ ...form, gst_hst_rate_pct: e.target.value })}
                    testid="gst-hst-rate-input"
                  />
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="property-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="property-create-submit">{saving ? "Creating…" : "Create property"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

